// Running a tour step's action (Help pane live tours). The runner calls the
// mounted page's tool directly, not through the Help bridge: the bridge turns
// a costly tool into a click-confirmed one and drops `confirmed` (PITFALLS in
// the plan), so the safety rule lives here.
//
// A tour shows the page; it never spends credits, never saves, never
// renames, copies or downloads, and never runs a tool the person must
// approve with a click. Such a step runs nothing and says why.
//
// An `ask` tool (the quick filter) runs with confirmed: true, and only
// because the step card told the person what Next would do ("Next: …")
// before they pressed it.

import type { VoiceUITool } from '../voice/types'
import type { TourAction } from './tourRuntime'
import { PICK_NOT_ON_PAGE, resolveArgs } from './tourPicks'

/** Tools no tour runs, whatever its meta says: they save, change names, copy or download. */
export const NEVER_IN_A_TOUR = [
  'save_table',
  'save_view_settings',
  'rename_toc_item',
  'duplicate_table',
  'create_folder',
  'download_folder_excel',
  'export_table_excel',
  'export_chart_pptx',
  'discard_changes',
  // Edit Spec's Cancel throws the edits away (Help pane live tours PR 2).
  'cancel_edit_spec',
  // Writes, downloads or a paid agent turn on other pages (validator, 14 Oct).
  'star_table',
  'download_file',
  'download_pipeline_csv',
  'send_message',
  'send_question',
  // Opening Insights asks for the table's summary: a model call on a cache
  // miss and on every filtered table (walkthroughs validator, 20 Oct).
  'toggle_insights',
] as const

const NEVER = new Set<string>(NEVER_IN_A_TOUR)

/** Whether a tour may run this tool on the person's behalf. */
export function isDemoSafe(tool: VoiceUITool): boolean {
  if (tool.meta?.costly || tool.meta?.confirm === 'click') return false
  return !NEVER.has(tool.name)
}

export type TourActionResult =
  | { did: string; picked: Record<string, string> }
  /** `retry`: the page may not be ready yet (its tools or its tree are still loading). */
  | { error: string; retry: boolean }

const capitalise = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

/** The "Did: …" line from a page tool's result. */
function didOf(result: Record<string, unknown>, action: TourAction): string {
  if (typeof result.opened === 'string') return `Opened ${result.opened}`
  if (typeof result.expanded === 'string') return `Opened the folder ${result.expanded}`
  if (typeof result.did === 'string' && result.did) return capitalise(result.did)
  return action.label
}

/** Run a step's action against the mounted page's tools. Never throws. */
export async function runTourAction(action: TourAction, tools: VoiceUITool[], picked: Record<string, string>): Promise<TourActionResult> {
  const tool = tools.find((t) => t.name === action.tool)
  if (!tool) return { error: PICK_NOT_ON_PAGE, retry: true }
  if (!isDemoSafe(tool)) return { error: `a tour does not run "${action.label}" for you; you can do it yourself`, retry: false }

  const resolved = await resolveArgs(action.args, tools, picked)
  if ('error' in resolved) return { error: resolved.error, retry: true }
  const args = tool.meta?.confirm === 'ask' ? { ...resolved.args, confirmed: true } : resolved.args

  let result: Record<string, unknown>
  try {
    result = JSON.parse(String(await tool.execute(args)))
  } catch (e) {
    return { error: e instanceof Error ? e.message : `${tool.name} failed`, retry: false }
  }
  if (typeof result.error === 'string') return { error: result.error, retry: /right now/.test(result.error) }
  if (result.needs_confirmation || result.ask_user || result.needs_disambiguation) {
    return { error: typeof result.message === 'string' ? result.message : 'it needs your answer first', retry: false }
  }
  return { did: didOf(result, action), picked: resolved.picked }
}
